import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONNECTION_INTENTS,
  type ConnectionIntent,
  type UserDoc,
} from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { TEXT_SCALES, useTheme } from '../../state/theme';
import { useI18n } from '../../i18n';
import { ScreenHeader, Section } from '../../components/common';
import { AvatarPicker } from './AvatarPicker';

/** Move one step along TEXT_SCALES, stopping at either end. */
function stepScale(current: number, delta: number): number {
  const at = TEXT_SCALES.indexOf(current as (typeof TEXT_SCALES)[number]);
  const next = Math.min(TEXT_SCALES.length - 1, Math.max(0, (at < 0 ? 1 : at) + delta));
  return TEXT_SCALES[next];
}

export function ProfileScreen() {
  const { t } = useI18n();
  const app = useApp();
  const theme = useTheme();
  const [user, setUser] = useState<UserDoc | null>(app.user);
  const [demoUsers, setDemoUsers] = useState<UserDoc[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => setUser(app.user), [app.user]);

  useEffect(() => {
    if (app.backend.listDemoUsers) {
      app.backend.listDemoUsers().then(setDemoUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.uid]);

  if (!user) {
    return (
      <div className="screen">
        <ScreenHeader title={t('profile.title')} />
        <div className="muted">{t('common.loading')}</div>
      </div>
    );
  }

  const toggleIntent = (intent: ConnectionIntent) => {
    setUser({
      ...user,
      intents: user.intents.includes(intent)
        ? user.intents.filter((i) => i !== intent)
        : [...user.intents, intent],
    });
  };

  /** Both Save buttons report into the profile card, so bring it into view. */
  const revealFeedback = () =>
    // Guarded: jsdom has no scrollIntoView, and it is a nicety — the message
    // is what actually tells someone what happened.
    cardRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });

  const save = async () => {
    if (!user) return;
    const displayName = user.displayName.trim();
    // Same contract as SocialOptInScreen: never disable for an unmet
    // requirement — answer the tap and name what is missing.
    if (displayName.length < 2) {
      setSaved(false);
      setError(t('form.needName'));
      revealFeedback();
      return;
    }
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const next = { ...user, displayName };
      await app.backend.saveUser(next);
      setUser(next);
      setSaved(true);
    } catch (e) {
      // A failed save used to be indistinguishable from a successful one.
      setError(String(e).replace(/^(FirebaseError|Error):\s*/, '').slice(0, 220));
    } finally {
      setSaving(false);
      revealFeedback();
    }
  };

  const togglePause = async () => {
    const paused = !user.recommendationsPaused;
    setUser({ ...user, recommendationsPaused: paused, discoverable: !paused });
    await app.backend.pauseRecommendations(paused);
  };

  const socialOn = user.socialEnabled === true;

  const disableSocial = async () => {
    const hasChats = app.conversations.length > 0;
    const message =
      t('social.disable.confirm') + (hasChats ? `\n\n${t('social.disable.keepChats')}` : '');
    if (!confirm(message)) return;
    setSaving(true);
    try {
      await app.setSocialEnabled(false);
    } catch (e) {
      alert(String(e).slice(0, 180));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader title={t('profile.title')} />

      {/* The display name is editable here because it was editable nowhere:
          a typo at onboarding followed people around permanently. */}
      <div className="card col" style={{ marginBottom: 8 }} ref={cardRef}>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          {/* The picker owns saving the picture, so it hands back the saved
              document rather than a signal — this screen keeps its own copy of
              the user in local state and would otherwise drift. Initials
              remain the fallback inside it for accounts without one. */}
          <AvatarPicker
            user={user}
            size={54}
            onSaved={(next) => {
              setError('');
              setSaved(false);
              setUser(next);
            }}
            hint={
              socialOn
                ? undefined
                : 'A picture is only needed if you turn connections on — discovery shows it.'
            }
          />
          <label className="field" style={{ flex: 1 }}>
            {t('onb.name')}
            <input
              className="input"
              maxLength={40}
              value={user.displayName}
              onChange={(e) => {
                setError('');
                setSaved(false);
                setUser({ ...user, displayName: e.target.value });
              }}
            />
          </label>
        </div>
        <div className="tiny muted">
          {user.city ?? user.region} · {user.languages.join(', ')} ·{' '}
          {app.chart?.meta.timeConfidence ?? '—'} birth time
        </div>
        {error && <div className="banner banner--danger small" role="alert">{error}</div>}
        {saved && (
          <div
            className="banner small"
            role="status"
            style={{ background: 'var(--ok-soft)', color: 'var(--ok)' }}
          >
            Saved.
          </div>
        )}
        <button
          className={`btn btn--primary btn--small ${user.displayName.trim().length < 2 ? 'btn--incomplete' : ''}`}
          style={{ alignSelf: 'flex-start' }}
          disabled={saving}
          onClick={save}
        >
          {saving ? <span className="spinner" /> : t('common.save')}
        </button>
      </div>

      <Section title={socialOn ? t('social.status.on') : t('social.status.off')}>
        <div className="card">
          <div className="spread" style={{ marginBottom: 8 }}>
            <div className="row">
              <span className={`chip ${socialOn ? 'chip--on' : 'chip--fact'}`} style={{ cursor: 'default' }}>
                {socialOn ? '◈ Connections on' : `🔒 ${t('social.private.badge')}`}
              </span>
            </div>
            {socialOn ? (
              <button className="btn btn--ghost btn--small" disabled={saving} onClick={disableSocial}>
                Turn off
              </button>
            ) : (
              <Link to="/social/enable">
                <button className="btn btn--primary btn--small">{t('social.private.cta')}</button>
              </Link>
            )}
          </div>
          <div className="tiny muted" style={{ lineHeight: 1.55 }}>
            {socialOn
              ? 'You are visible to a small number of compatible people, and can send and receive connection requests.'
              : t('social.private.body')}
          </div>
        </div>
      </Section>

      {socialOn && (
        <>
          <Section title={t('profile.editIntents')}>
            <div className="card">
              <div className="wrap">
                {CONNECTION_INTENTS.map((intent) => (
                  <button
                    key={intent}
                    className={`chip ${user.intents.includes(intent) ? 'chip--on' : ''}`}
                    onClick={() => toggleIntent(intent)}
                  >
                    {intent.replace(/-/g, ' ')}
                  </button>
                ))}
              </div>
              <button
                className="btn btn--primary btn--small"
                style={{ marginTop: 12 }}
                disabled={saving}
                onClick={save}
              >
                {saving ? <span className="spinner" /> : t('common.save')}
              </button>
            </div>
          </Section>

          <Section title="Recommendations">
            <div className="card">
              <div className="spread">
                <div>
                  <div className="h3">{t('profile.pause')}</div>
                  <div className="tiny muted">{t('profile.pause.note')}</div>
                </div>
                <button
                  className={`chip ${user.recommendationsPaused ? 'chip--on' : ''}`}
                  onClick={togglePause}
                >
                  {user.recommendationsPaused ? 'Paused' : 'Active'}
                </button>
              </div>
            </div>
          </Section>
        </>
      )}

      <Section title={t('theme.title')}>
        <div className="seg" role="group" aria-label={t('theme.title')}>
          {(['system', 'light', 'dark'] as const).map((opt) => (
            <button
              key={opt}
              className={`seg__opt ${theme.choice === opt ? 'seg__opt--on' : ''}`}
              aria-pressed={theme.choice === opt}
              onClick={() => theme.setChoice(opt)}
            >
              {t(`theme.${opt}`)}
            </button>
          ))}
        </div>
        <div className="tiny faint" style={{ marginTop: 6 }}>
          {theme.choice === 'system' ? t('theme.systemHelp') : t('theme.manualHelp')}
        </div>

        {/* Text size. Steps rather than a slider: a slider on a surface that
            resizes as you drag is genuinely unpleasant to use, and five named
            sizes cover the range anyone needs. */}
        <div className="spread" style={{ marginTop: 16, marginBottom: 6 }}>
          <span className="h3">{t('theme.textSize')}</span>
          <span className="tiny faint">{Math.round(theme.textScale * 100)}%</span>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn btn--ghost btn--small"
            aria-label={t('theme.textSmaller')}
            disabled={theme.textScale === TEXT_SCALES[0]}
            onClick={() => theme.setTextScale(stepScale(theme.textScale, -1))}
          >
            <span aria-hidden style={{ fontSize: '0.85rem' }}>A</span>
            <span aria-hidden>−</span>
          </button>
          <div className="seg" role="group" aria-label={t('theme.textSize')} style={{ flex: 1 }}>
            {TEXT_SCALES.map((s) => (
              <button
                key={s}
                className={`seg__opt ${theme.textScale === s ? 'seg__opt--on' : ''}`}
                aria-pressed={theme.textScale === s}
                aria-label={`${Math.round(s * 100)}%`}
                onClick={() => theme.setTextScale(s)}
              >
                <span aria-hidden style={{ fontSize: `${0.7 + (s - 0.9) * 0.9}rem` }}>A</span>
              </button>
            ))}
          </div>
          <button
            className="btn btn--ghost btn--small"
            aria-label={t('theme.textLarger')}
            disabled={theme.textScale === TEXT_SCALES[TEXT_SCALES.length - 1]}
            onClick={() => theme.setTextScale(stepScale(theme.textScale, 1))}
          >
            <span aria-hidden style={{ fontSize: '1.15rem' }}>A</span>
            <span aria-hidden>+</span>
          </button>
        </div>
        <div className="tiny faint" style={{ marginTop: 6 }}>
          {t('theme.textSizeHelp')}
        </div>
      </Section>

      <Section title="Astrally">
        <div className="col" style={{ gap: 0 }}>
          {/* Curriculum above glossary: the guided path is the front door,
              the alphabetical reference is the shelf behind it. */}
          <Link to="/learn" style={{ color: 'inherit' }}>
            <div className="list-item"><span>🎓</span><span style={{ flex: 1 }}>{t('learn.title')}</span><span className="faint">›</span></div>
          </Link>
          <Link to="/glossary" style={{ color: 'inherit' }}>
            <div className="list-item"><span>📖</span><span style={{ flex: 1 }}>{t('glossary.title')}</span><span className="faint">›</span></div>
          </Link>
          <Link to="/profile/memory" style={{ color: 'inherit' }}>
            <div className="list-item"><span>🧠</span><span style={{ flex: 1 }}>{t('profile.memory')}</span><span className="faint">›</span></div>
          </Link>
          <Link to="/profile/refine" style={{ color: 'inherit' }}>
            <div className="list-item"><span>⏱</span><span style={{ flex: 1 }}>{t('profile.refine')}</span><span className="faint">›</span></div>
          </Link>
          <Link to="/profile/privacy" style={{ color: 'inherit' }}>
            <div className="list-item"><span>🔒</span><span style={{ flex: 1 }}>{t('profile.privacy')}</span><span className="faint">›</span></div>
          </Link>
          <Link to="/profile/subscription" style={{ color: 'inherit' }}>
            <div className="list-item"><span>◆</span><span style={{ flex: 1 }}>{t('pro.title')}</span><span className="faint">›</span></div>
          </Link>
          <Link to="/profile/credits" style={{ color: 'inherit' }}>
            <div className="list-item"><span>◈</span><span style={{ flex: 1 }}>{t('credits.title')}</span><span className="faint">›</span></div>
          </Link>
        </div>
      </Section>

      {app.backend.kind === 'demo' && demoUsers.length > 0 && (
        <Section title={t('profile.switchDemo')}>
          <div className="card">
            <div className="tiny muted" style={{ marginBottom: 8 }}>
              Demo-only: switch accounts to accept an introduction as the other person and open a
              two-way conversation.
            </div>
            <div className="wrap">
              {demoUsers.map((u) => (
                <button
                  key={u.uid}
                  className={`chip ${u.uid === app.uid ? 'chip--on' : ''}`}
                  onClick={() => app.switchDemoUser(u.uid)}
                >
                  {u.displayName}
                </button>
              ))}
            </div>
          </div>
        </Section>
      )}

      <div className="tiny faint center" style={{ marginTop: 20 }}>
        {t('safety.disclaimer')}
      </div>
    </div>
  );
}
